import { useMedTypes, isStripType, unitLabelOf, looseLabelOf, unitKeyOf } from "@/lib/medTypes";

import { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Search, Plus, Minus, Trash2, ShoppingCart, X, User, Phone, Stethoscope, CheckCircle2, Receipt, QrCode, IndianRupee, CreditCard, Wallet, Loader2, Printer, Pill, BookUser, MessageCircle } from "lucide-react";
import { toast } from "sonner";
import { QRCodeCanvas } from "qrcode.react";

const fmtRs = (v) => `₹${Number(v || 0).toFixed(2)}`;

export default function POS() {
  const [medicines, setMedicines] = useState([]);
  const [query, setQuery] = useState("");
  const [cart, setCart] = useState([]);
  const [customer, setCustomer] = useState({ name: "Walk-in Customer", phone: "", doctor: "" });
  const [discountAmount, setDiscountAmount] = useState(0);
  const [paidAmount, setPaidAmount] = useState("");
  const [checkoutOpen, setCheckoutOpen] = useState(false);
  const [paymentMode, setPaymentMode] = useState("cash");
  const [processing, setProcessing] = useState(false);
  const [lastInvoice, setLastInvoice] = useState(null);
  const [receiptOpen, setReceiptOpen] = useState(false);
  const [settings, setSettings] = useState(null);

  useEffect(() => {
    api.get("/medicines").then(({ data }) => setMedicines(data));
    api.get("/settings").then(({ data }) => setSettings(data));
  }, []);

  const [category, setCategory] = useState("All");
  const categories = useMemo(
    () => ["All", ...Array.from(new Set(medicines.map((m) => m.category).filter(Boolean)))],
    [medicines]
  );
  const results = useMemo(() => {
    let list = category === "All" ? medicines : medicines.filter((m) => m.category === category);
    if (!query.trim()) return list.slice(0, 20);
    const q = query.toLowerCase();
    return list
      .filter((m) => m.name.toLowerCase().includes(q) || m.generic_name?.toLowerCase().includes(q) || m.batch_no?.toLowerCase().includes(q))
      .slice(0, 20);
  }, [query, medicines, category]);

  // Barcode scanner: exact barcode match auto-adds to cart
  useEffect(() => {
    const q = query.trim();
    if (q.length < 4) return;
    const exact = medicines.find((m) => m.barcode && String(m.barcode) === q);
    if (exact) {
      addToCart(exact, isStripType(exact.med_type) ? "strip" : unitKeyOf(exact));
      setQuery("");
      toast.success(`${exact.name} added (barcode)`);
    }
    // eslint-disable-next-line
  }, [query, medicines]);

  const addToCart = (m, unit = "strip") => {
    const stripLike = isStripType(m.med_type);
    if (!stripLike) unit = unitKeyOf(m);
    const availTabs = stripLike ? m.stock_strips * m.pack_size + m.stock_loose_tablets : m.stock_strips;
    if (availTabs <= 0) { toast.error("Out of stock"); return; }
    const key = `${m.id}_${unit}`;
    setCart((prev) => {
      const existing = prev.find((i) => i.key === key);
      if (existing) {
        return prev.map((i) => (i.key === key ? { ...i, quantity: i.quantity + 1 } : i));
      }
      const unit_price = stripLike ? (unit === "strip" ? m.strip_mrp : m.strip_mrp / m.pack_size) : m.strip_mrp;
      return [
        ...prev,
        {
          key, medicine_id: m.id, name: m.name, hsn_code: m.hsn_code,
          batch_no: m.batch_no, expiry_date: m.expiry_date,
          unit, unit_label: stripLike ? (unit === "strip" ? "Strip" : looseLabelOf(m)) : unitLabelOf(m),
          quantity: 1, pack_size: m.pack_size,
          unit_price: Number(unit_price.toFixed(2)), gst_percent: m.gst_percent,
          discount_percent: 0, _stock_tabs: availTabs,
        },
      ];
    });
  };

  const updateQty = (key, delta) => {
    setCart((prev) =>
      prev
        .map((i) => (i.key === key ? { ...i, quantity: Math.max(0, i.quantity + delta) } : i))
        .filter((i) => i.quantity > 0)
    );
  };

  const removeLine = (key) => setCart((prev) => prev.filter((i) => i.key !== key));

  const totals = useMemo(() => {
    let sub = 0, gst = 0, disc = 0, grand = 0;
    cart.forEach((it) => {
      const gross = it.unit_price * it.quantity;
      const d = gross * (it.discount_percent / 100);
      const tax = (gross - d) * (it.gst_percent / 100);
      sub += gross - d;
      gst += tax;
      disc += d;
      grand += gross - d + tax;
    });
    grand = Math.max(0, grand - Number(discountAmount || 0));
    return { sub, gst, disc: disc + Number(discountAmount || 0), grand };
  }, [cart, discountAmount]);

  const [splitPay, setSplitPay] = useState({ cash: "", card: "", upi: "", credit: "" });
  const [creditDueDate, setCreditDueDate] = useState("");
  const splitSum = Number(splitPay.cash || 0) + Number(splitPay.card || 0) + Number(splitPay.upi || 0) + Number(splitPay.credit || 0);
  const cardFee = Math.round((paymentMode === "card" ? totals.grand : paymentMode === "split" ? Number(splitPay.card || 0) : 0) * 3) / 100;
  const payable = totals.grand + cardFee;
  const splitCredit = Number(splitPay.credit || 0);
  useMedTypes();
  const [pendingOnline, setPendingOnline] = useState([]);
  useEffect(() => {
    const t = () => api.get("/online-orders", { params: { status: "accepted" } }).then(({ data }) => setPendingOnline(data)).catch(() => {});
    t();
    const iv = setInterval(t, 30000);
    return () => clearInterval(iv);
  }, []);

  const billOnline = async (o) => {
    try {
      const { data } = await api.post(`/online-orders/${o.id}/create-invoice`);
      toast.success(`Invoice ${data.invoice_no} ban gaya — Invoices page par print karein`);
      setPendingOnline((p) => p.filter((x) => x.id !== o.id));
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Bill nahi ban paya");
    }
  };

  const checkout = async () => {
    if (cart.length === 0) return toast.error("Cart is empty");
    if (paymentMode === "credit" && !customer.phone) return toast.error("Customer phone is required for a credit sale");
    setProcessing(true);
    try {
      const payload = {
        customer_name: customer.name,
        customer_phone: customer.phone,
        doctor: customer.doctor,
        items: cart.map(({ key, _stock_tabs, ...rest }) => rest),
        payment_mode: paymentMode,
        discount_amount: Number(discountAmount || 0),
        paid_amount: paymentMode === "credit" ? Number(paidAmount || 0) : null,
        credit_due_date: paymentMode === "credit" ? creditDueDate : "",
        branch_id: localStorage.getItem("branch_id") || "",
        branch_name: localStorage.getItem("branch_name") || "",
      };
      if (paymentMode === "split") {
        const sp = Object.fromEntries(Object.entries(splitPay).map(([k, v]) => [k, Number(v || 0)]).filter(([, v]) => v > 0));
        const sum = Object.values(sp).reduce((a, b) => a + b, 0);
        if (Object.keys(sp).length === 0) { toast.error("Split mein kam se kam 1 mode ka amount daalein"); setProcessing(false); return; }
        if (Number(sp.credit || 0) > 0 && !customer.phone) { toast.error("Split mein udhaar ke liye customer phone number zaroori hai"); setProcessing(false); return; }
        if (Math.abs(sum - payable) > 0.5) { toast.error(`Split total (₹${sum.toFixed(2)}) bill total + card fee (₹${payable.toFixed(2)}) se match nahi hota`); setProcessing(false); return; }
        payload.payment_split = sp;
        if (Number(sp.credit || 0) > 0) payload.credit_due_date = creditDueDate;
      }
      const { data } = await api.post("/invoices", payload);
      setLastInvoice(data);
      setReceiptOpen(true);
      setCheckoutOpen(false);
      // Reset
      setCart([]);
      setDiscountAmount(0);
      setCustomer({ name: "Walk-in Customer", phone: "", doctor: "" });
      setSplitPay({ cash: "", card: "", upi: "", credit: "" });
      setCreditDueDate("");
      // Reload medicines to update stock
      const { data: meds } = await api.get("/medicines");
      setMedicines(meds);
      toast.success(`Invoice ${data.invoice_no} generated`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Checkout failed");
    } finally {
      setProcessing(false);
    }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-5 gap-4" data-testid="pos-page">
      {/* Left: Search + Results */}
      <div className="lg:col-span-3 space-y-4">
        <div>
          <h1 className="font-display text-2xl font-extrabold tracking-tight text-slate-900">POS Billing</h1>
          <p className="text-sm text-slate-500">Instant medicine search, strip &amp; tablet quantity, live stock deduction.</p>
        </div>

        <Card className="p-4 border-slate-200">
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input
              autoFocus
              data-testid="pos-search-input"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search medicine by name, salt, or batch…"
              className="pl-9 h-11"
            />
          </div>
        </Card>

        <div className="flex gap-2 flex-wrap" data-testid="pos-category-chips">
          {categories.map((c) => (
            <button
              key={c}
              data-testid={`pos-cat-${c.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
              onClick={() => setCategory(c)}
              className={`px-3.5 h-9 rounded-full text-xs font-semibold border transition-all ${category === c ? "bg-emerald-900 text-white border-emerald-900 shadow-sm" : "bg-white text-slate-600 border-slate-200 hover:border-emerald-500"}`}
            >
              {c}
            </button>
          ))}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 max-h-[65vh] overflow-y-auto pr-1">
          {results.map((m) => {
            const stripLike = isStripType(m.med_type);
            const totalTabs = stripLike ? m.stock_strips * m.pack_size + m.stock_loose_tablets : m.stock_strips;
            const low = m.stock_strips < 10;
            return (
              <Card key={m.id} data-testid={`med-card-${m.id}`} className="p-4 border-slate-200 hover:border-emerald-600/60 hover:shadow-sm transition-all">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="font-semibold text-slate-900 truncate">{m.name}</div>
                    <div className="text-xs text-slate-500 truncate">{m.generic_name} • {m.manufacturer}</div>
                    <div className="text-[11px] text-slate-400 mt-0.5">Batch {m.batch_no || "—"} • Exp {m.expiry_date || "—"}</div>
                  </div>
                  {totalTabs === 0 ? (
                    <Badge variant="destructive">Out</Badge>
                  ) : low ? (
                    <Badge className="bg-amber-100 text-amber-800 hover:bg-amber-100">Low</Badge>
                  ) : (
                    <Badge className="bg-emerald-100 text-emerald-800 hover:bg-emerald-100">In</Badge>
                  )}
                </div>
                <div className="mt-3 flex items-center justify-between">
                  <div>
                    <div className="font-mono-num font-bold text-slate-900">{fmtRs(m.strip_mrp)}<span className="text-xs text-slate-500 font-normal"> /{unitLabelOf(m).toLowerCase()}</span></div>
                    <div className="text-[11px] text-slate-500">Stock: {m.stock_strips} {unitLabelOf(m).toLowerCase()}s{stripLike ? ` + ${m.stock_loose_tablets} loose` : ""}</div>
                  </div>
                  <div className="flex gap-1.5">
                    {stripLike ? (
                      <>
                        <Button size="sm" variant="outline" data-testid={`add-tab-${m.id}`} disabled={totalTabs === 0} onClick={() => addToCart(m, "tablet")} className="h-8 border-emerald-200 text-emerald-800 hover:bg-emerald-50">+ {looseLabelOf(m)}</Button>
                        <Button size="sm" data-testid={`add-strip-${m.id}`} disabled={m.stock_strips === 0} onClick={() => addToCart(m, "strip")} className="h-8 bg-emerald-900 hover:bg-emerald-800">+ Strip</Button>
                      </>
                    ) : (
                      <Button size="sm" data-testid={`add-unit-${m.id}`} disabled={totalTabs === 0} onClick={() => addToCart(m)} className="h-8 bg-emerald-900 hover:bg-emerald-800">+ {unitLabelOf(m)}</Button>
                    )}
                  </div>
                </div>
              </Card>
            );
          })}
          {results.length === 0 && (
            <Card className="p-6 border-slate-200 col-span-full text-center">
              <p className="text-slate-500 text-sm">No medicines found. Try a different search.</p>
            </Card>
          )}
        </div>
      </div>

      {/* Right: Cart */}
      <div className="lg:col-span-2 space-y-3">
        {pendingOnline.length > 0 && (
          <Card className="border-indigo-200 bg-indigo-50" data-testid="pos-pending-orders">
            <div className="p-3 border-b border-indigo-100 flex items-center gap-2">
              <span className="text-sm font-bold text-indigo-900">Online Orders — Packing Pending ({pendingOnline.length})</span>
            </div>
            <div className="p-3 space-y-2 max-h-48 overflow-y-auto">
              {pendingOnline.map((o) => (
                <div key={o.id} className="bg-white rounded-xl border border-indigo-100 p-2.5 flex items-center gap-2" data-testid={`pos-pending-${o.id}`}>
                  <div className="flex-1 min-w-0">
                    <div className="text-xs font-bold text-slate-900">{o.order_no} • {o.customer_name}</div>
                    <div className="text-[11px] text-slate-500 truncate">{o.items.map((i) => `${i.medicine_name} ×${i.quantity}`).join(", ")}</div>
                  </div>
                  <Button size="sm" data-testid={`pos-bill-${o.id}`} onClick={() => billOnline(o)} className="h-8 bg-emerald-900 hover:bg-emerald-800 shrink-0">Bill Banao</Button>
                </div>
              ))}
            </div>
          </Card>
        )}
        <Card className="border-slate-200 sticky top-4">
          <div className="p-4 border-b border-slate-100 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <ShoppingCart className="w-4 h-4 text-emerald-700" />
              <h3 className="font-display font-bold">Current Invoice</h3>
            </div>
            <Badge variant="secondary" data-testid="cart-count">{cart.length}</Badge>
          </div>

          <div className="p-4 space-y-3">
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-xs">Customer</Label>
                <div className="relative mt-1">
                  <User className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
                  <Input data-testid="cust-name" className="pl-8 h-9" value={customer.name} onChange={(e) => setCustomer({ ...customer, name: e.target.value })} />
                </div>
              </div>
              <div>
                <Label className="text-xs">Phone</Label>
                <div className="relative mt-1">
                  <Phone className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
                  <Input data-testid="cust-phone" className="pl-8 h-9" value={customer.phone} onChange={(e) => setCustomer({ ...customer, phone: e.target.value })} />
                </div>
              </div>
            </div>
            <div>
              <Label className="text-xs">Doctor (optional)</Label>
              <div className="relative mt-1">
                <Stethoscope className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
                <Input className="pl-8 h-9" value={customer.doctor} onChange={(e) => setCustomer({ ...customer, doctor: e.target.value })} placeholder="Dr. …" />
              </div>
            </div>
          </div>

          <div className="max-h-[38vh] overflow-y-auto border-t border-slate-100">
            {cart.length === 0 ? (
              <div className="p-6 text-center text-sm text-slate-400">Cart is empty. Search and add medicines.</div>
            ) : (
              cart.map((it) => (
                <div key={it.key} data-testid="pos-cart-item" className="p-3 border-b border-slate-100 last:border-0">
                  <div className="flex justify-between gap-2">
                    <div className="min-w-0">
                      <div className="font-semibold text-sm text-slate-900 truncate">{it.name}</div>
                      <div className="text-xs text-slate-500">{it.unit_label || (it.unit === "strip" ? "Strip" : "Tablet")} • {fmtRs(it.unit_price)} × {it.quantity} • GST {it.gst_percent}%</div>
                    </div>
                    <button onClick={() => removeLine(it.key)} className="text-slate-400 hover:text-red-600">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                  <div className="mt-2 flex items-center gap-2">
                    <Button size="icon" variant="outline" className="h-7 w-7" onClick={() => updateQty(it.key, -1)}><Minus className="w-3 h-3" /></Button>
                    <div className="w-10 text-center font-mono-num font-bold">{it.quantity}</div>
                    <Button size="icon" variant="outline" className="h-7 w-7" onClick={() => updateQty(it.key, 1)}><Plus className="w-3 h-3" /></Button>
                    <div className="ml-auto font-mono-num font-bold text-slate-900">{fmtRs(it.unit_price * it.quantity * (1 + it.gst_percent / 100))}</div>
                  </div>
                </div>
              ))
            )}
          </div>

          <div className="p-4 border-t border-slate-100 space-y-1.5 bg-slate-50">
            <Row label="Subtotal" value={fmtRs(totals.sub)} />
            <Row label="GST" value={fmtRs(totals.gst)} />
            <Row label="Discount" value={`- ${fmtRs(totals.disc)}`} />
            <div className="flex gap-2 items-center pt-1">
              <Label className="text-xs">Extra ₹ off</Label>
              <Input type="number" min="0" className="h-8 w-24 font-mono-num" value={discountAmount} onChange={(e) => setDiscountAmount(e.target.value)} />
            </div>
            <div className="flex items-center justify-between pt-2 border-t border-slate-200 mt-2">
              <span className="text-sm text-slate-600">Payable</span>
              <span data-testid="cart-grand-total" className="font-mono-num text-2xl font-extrabold text-emerald-900">{fmtRs(totals.grand)}</span>
            </div>
            <Button
              data-testid="pos-checkout-btn"
              disabled={cart.length === 0}
              onClick={() => setCheckoutOpen(true)}
              className="w-full h-11 mt-3 bg-emerald-900 hover:bg-emerald-800 text-white"
            >
              <Receipt className="w-4 h-4 mr-2" /> Checkout & Generate Invoice
            </Button>
          </div>
        </Card>
      </div>

      {/* Checkout dialog */}
      <Dialog open={checkoutOpen} onOpenChange={setCheckoutOpen}>
        <DialogContent data-testid="checkout-dialog" className="max-w-md bg-white">
          <DialogHeader>
            <DialogTitle className="font-display">Complete Payment</DialogTitle>
            <DialogDescription className="sr-only">Select payment mode, split amounts and generate the invoice</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="p-4 rounded-lg bg-emerald-50 border border-emerald-100 flex items-center justify-between">
              <span className="text-emerald-800 font-medium">Amount Due</span>
              <div className="text-right">
                <span className="font-mono-num text-2xl font-extrabold text-emerald-900" data-testid="amount-due-value">{fmtRs(payable)}</span>
                {cardFee > 0 && <div className="text-[11px] text-emerald-700 font-medium" data-testid="amount-due-fee">includes card fee (3%): {fmtRs(cardFee)}</div>}
              </div>
            </div>

            <div>
              <Label className="text-xs uppercase tracking-wider">Payment mode</Label>
              <div className="grid grid-cols-3 gap-2 mt-2">
                {[
                  { id: "cash", label: "Cash", icon: Wallet },
                  { id: "card", label: "Card", icon: CreditCard },
                  { id: "upi", label: "UPI QR", icon: QrCode },
                  { id: "phonepe", label: "PhonePe", icon: IndianRupee },
                  { id: "gpay", label: "GPay", icon: IndianRupee },
                  { id: "scanner", label: "Scanner", icon: QrCode },
                  { id: "credit", label: "Credit", icon: BookUser },
                  { id: "split", label: "Split", icon: Wallet },
                ].map((p) => (
                  <button
                    key={p.id}
                    data-testid={`payment-mode-${p.id}`}
                    onClick={() => setPaymentMode(p.id)}
                    className={`p-2.5 rounded-lg border text-xs font-semibold flex flex-col items-center gap-1 transition-all ${paymentMode === p.id ? "border-emerald-700 bg-emerald-50 text-emerald-900" : "border-slate-200 bg-white text-slate-600 hover:border-slate-300"}`}
                  >
                    <p.icon className="w-4 h-4" />
                    {p.label}
                  </button>
                ))}
              </div>
            </div>

            {paymentMode === "split" && (
              <div className="p-4 rounded-lg border border-sky-200 bg-sky-50 space-y-2" data-testid="split-panel">
                <div className="text-xs font-semibold text-sky-800 uppercase tracking-wide">Split Payment — total {fmtRs(payable)} hona chahiye</div>
                {[
                  { k: "cash", label: "Cash" },
                  { k: "card", label: "Card" },
                  { k: "upi", label: "UPI" },
                  { k: "credit", label: "Udhaar" },
                ].map(({ k, label }) => (
                  <div key={k}>
                    <div className="flex items-center gap-2">
                      <Label className="text-xs w-12 uppercase shrink-0">{label}</Label>
                      <Input data-testid={`split-${k}-input`} type="number" min="0" value={splitPay[k]} onChange={(e) => setSplitPay({ ...splitPay, [k]: e.target.value })} className="h-9 font-mono-num bg-white" placeholder="0" />
                    </div>
                    {k === "card" && Number(splitPay.card || 0) > 0 && (
                      <div className="flex justify-between text-[11px] text-slate-500 pl-14 pt-0.5" data-testid="split-card-fee">
                        <span>Card Fee (3%)</span>
                        <span className="font-mono-num">+{fmtRs(cardFee)}</span>
                      </div>
                    )}
                  </div>
                ))}
                {splitCredit > 0 && (
                  <div className="space-y-1.5 pt-1 border-t border-sky-200">
                    <div className="flex items-center gap-2">
                      <Label className="text-xs w-12 uppercase shrink-0">Due</Label>
                      <Input data-testid="split-credit-due-date" type="date" value={creditDueDate} onChange={(e) => setCreditDueDate(e.target.value)} className="h-9 bg-white font-mono-num" min={new Date().toISOString().slice(0, 10)} />
                    </div>
                    {!customer.phone && <div className="text-xs text-red-600">Udhaar ke liye customer phone number zaroori hai</div>}
                  </div>
                )}
                <div className="flex justify-between text-sm pt-1 border-t border-sky-200">
                  <span className="text-sky-700">Baaki (remaining)</span>
                  <span className={`font-mono-num font-bold ${Math.abs(splitSum - payable) <= 0.5 ? "text-emerald-700" : "text-red-600"}`} data-testid="split-remaining">{fmtRs(Math.max(0, payable - splitSum))}</span>
                </div>
              </div>
            )}

            {paymentMode === "card" && totals.grand > 0 && (
              <div className="p-3 rounded-lg border border-slate-200 bg-slate-50 flex items-center justify-between text-sm" data-testid="card-fee-info">
                <span className="text-slate-600">Card Fee (3%) — customer se extra liya jayega</span>
                <span className="font-mono-num font-bold text-slate-900">+{fmtRs(cardFee)}</span>
              </div>
            )}

            {paymentMode === "credit" && (
              <div className="p-4 rounded-lg border border-amber-200 bg-amber-50 space-y-2" data-testid="credit-panel">
                <div className="text-xs font-semibold text-amber-800 uppercase tracking-wide">Credit Sale</div>
                <div className="flex items-center gap-2">
                  <Label className="text-xs shrink-0">Paid now (₹)</Label>
                  <Input data-testid="credit-paid-input" type="number" min="0" max={totals.grand} value={paidAmount} onChange={(e) => setPaidAmount(e.target.value)} className="h-9 font-mono-num bg-white" placeholder="0" />
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-amber-700">Balance due</span>
                  <span className="font-mono-num font-bold text-red-700" data-testid="credit-due-preview">
                    ₹{Math.max(0, totals.grand - Number(paidAmount || 0)).toFixed(2)}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <Label className="text-xs shrink-0">Pay by date</Label>
                  <Input data-testid="credit-due-date-input" type="date" value={creditDueDate} onChange={(e) => setCreditDueDate(e.target.value)} className="h-9 bg-white font-mono-num" min={new Date().toISOString().slice(0, 10)} />
                </div>
                {!customer.phone && <div className="text-xs text-red-600">Customer phone number is required for credit sales</div>}
              </div>
            )}

            {["upi", "phonepe", "gpay", "scanner"].includes(paymentMode) && settings?.upi_id && (
              <div className="p-4 rounded-lg border border-slate-200 bg-white flex flex-col items-center gap-2" data-testid="upi-qr-modal">
                <div className="text-xs text-slate-500">Scan to pay {fmtRs(totals.grand)}</div>
                <div className="p-3 bg-white border border-slate-200 rounded-xl">
                  <QRCodeCanvas
                    data-testid="upi-qr-code-container"
                    value={`upi://pay?pa=${encodeURIComponent(settings.upi_id)}&pn=${encodeURIComponent(settings.upi_payee_name || "Trustmeds Pharma")}&am=${totals.grand.toFixed(2)}&cu=INR&tn=Invoice`}
                    size={180}
                    level="M"
                  />
                </div>
                <div className="text-xs text-slate-500 font-mono-num">{settings.upi_id}</div>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="ghost" onClick={() => setCheckoutOpen(false)}>Cancel</Button>
            <Button data-testid="confirm-payment-btn" onClick={checkout} disabled={processing} className="bg-emerald-900 hover:bg-emerald-800">
              {processing ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Confirm & Generate</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Receipt dialog */}
      <Dialog open={receiptOpen} onOpenChange={setReceiptOpen}>
        <DialogContent className="max-w-lg bg-white" data-testid="receipt-dialog">
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-2"><CheckCircle2 className="w-5 h-5 text-emerald-600" /> Invoice Generated</DialogTitle>
            <DialogDescription className="sr-only">Invoice receipt with print and WhatsApp share options</DialogDescription>
          </DialogHeader>
          {lastInvoice && <Receipt80mm invoice={lastInvoice} settings={settings} />}
          <DialogFooter>
            <Button variant="outline" onClick={() => setReceiptOpen(false)}>Close</Button>
            {lastInvoice?.customer_phone && (
              <Button
                variant="outline"
                data-testid="whatsapp-share-btn"
                className="border-emerald-300 text-emerald-800 hover:bg-emerald-50"
                onClick={() => {
                  const phone = lastInvoice.customer_phone.replace(/\D/g, "");
                  const p = phone.length === 10 ? `91${phone}` : phone;
                  const lines = [
                    `*${settings?.pharmacy_name || "Trustmeds Pharma"}*`,
                    `Invoice: ${lastInvoice.invoice_no}`,
                    `Date: ${new Date(lastInvoice.created_at).toLocaleString("en-IN")}`,
                    ...lastInvoice.items.map((it) => `• ${it.name} x${it.quantity} — ₹${Number(it.total).toFixed(2)}`),
                    `*Total: ₹${lastInvoice.grand_total.toFixed(2)}*`,
                    `Payment: ${lastInvoice.payment_mode.toUpperCase()}`,
                    "", "Thank you! Get well soon.",
                  ];
                  window.open(`https://wa.me/${p}?text=${encodeURIComponent(lines.join("\n"))}`, "_blank");
                }}
              >
                <MessageCircle className="w-4 h-4 mr-2" /> WhatsApp
              </Button>
            )}
            <Button data-testid="invoice-print-btn" onClick={() => window.print()} className="bg-emerald-900 hover:bg-emerald-800">
              <Printer className="w-4 h-4 mr-2" /> Print
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex justify-between text-sm">
      <span className="text-slate-500">{label}</span>
      <span className="font-mono-num text-slate-900">{value}</span>
    </div>
  );
}

export function Receipt80mm({ invoice, settings }) {
  return (
    <div className="print-area print-thermal max-h-[70vh] overflow-y-auto text-slate-900">
      <div className="border border-slate-200 rounded-lg p-5 bg-white">
        <div className="text-center border-b border-dashed border-slate-300 pb-3">
          <div className="flex items-center justify-center gap-2">
            <div className="w-9 h-9 rounded-lg bg-emerald-900 flex items-center justify-center">
              <Pill className="w-5 h-5 text-white" />
            </div>
            <div className="font-display font-extrabold text-lg text-emerald-950">{settings?.pharmacy_name || "Trustmeds Pharma"}</div>
          </div>
          {settings?.tagline && <div className="text-[10px] text-slate-400 italic mt-0.5">{settings.tagline}</div>}
          {settings?.address && <div className="text-xs text-slate-600 mt-1">{settings.address}</div>}
          <div className="text-xs text-slate-500">
            {settings?.gstin && <>GSTIN: {settings.gstin} • </>}
            {settings?.drug_license && <>DL: {settings.drug_license}</>}
          </div>
          {settings?.phone && <div className="text-xs text-slate-500">Ph: {settings.phone}</div>}
        </div>

        <div className="mt-3 flex justify-between text-xs">
          <div>
            <div><span className="text-slate-500">Invoice: </span><span className="font-mono-num font-bold">{invoice.invoice_no}</span></div>
            <div><span className="text-slate-500">Date: </span>{new Date(invoice.created_at).toLocaleString("en-IN")}</div>
          </div>
          <div className="text-right">
            <div>{invoice.customer_name}</div>
            <div className="text-slate-500">{invoice.customer_phone}</div>
            {invoice.doctor && <div className="text-slate-500">{invoice.doctor}</div>}
          </div>
        </div>

        <table className="w-full text-xs mt-3">
          <thead>
            <tr className="border-y border-slate-200">
              <th className="text-left py-1">Item</th>
              <th className="text-right">Qty</th>
              <th className="text-right">Rate</th>
              <th className="text-right">Total</th>
            </tr>
          </thead>
          <tbody>
            {invoice.items.map((it, i) => (
              <tr key={i} className="border-b border-slate-100">
                <td className="py-1.5">
                  {it.name}
                  <div className="text-slate-400 text-[10px]">Batch {it.batch_no || "—"} • {it.unit_label || it.unit} • GST {it.gst_percent}%</div>
                </td>
                <td className="text-right font-mono-num">{it.quantity}</td>
                <td className="text-right font-mono-num">₹{Number(it.unit_price).toFixed(2)}</td>
                <td className="text-right font-mono-num">₹{Number(it.total).toFixed(2)}</td>
              </tr>
            ))}
          </tbody>
        </table>

        <div className="mt-3 space-y-1 text-xs">
          <div className="flex justify-between"><span className="text-slate-500">Subtotal</span><span className="font-mono-num">₹{invoice.subtotal.toFixed(2)}</span></div>
          <div className="flex justify-between"><span className="text-slate-500">GST</span><span className="font-mono-num">₹{invoice.gst_total.toFixed(2)}</span></div>
          <div className="flex justify-between"><span className="text-slate-500">Discount</span><span className="font-mono-num">-₹{invoice.discount_total.toFixed(2)}</span></div>
          {Number(invoice.card_fee || 0) > 0 && (
            <div className="flex justify-between" data-testid="receipt-card-fee"><span className="text-slate-500">Card Fee (3%)</span><span className="font-mono-num">₹{Number(invoice.card_fee).toFixed(2)}</span></div>
          )}
          <div className="flex justify-between border-t border-slate-200 pt-1 mt-1"><span className="font-bold">Grand Total</span><span className="font-mono-num font-extrabold text-emerald-900">₹{invoice.grand_total.toFixed(2)}</span></div>
          <div className="flex justify-between"><span className="text-slate-500">Payment</span><span className="font-medium uppercase">{invoice.payment_mode}</span></div>
          {invoice.payment_split && (
            <div className="text-[10px] text-slate-500 text-right" data-testid="receipt-split-breakup">
              {Object.entries(invoice.payment_split).map(([k, v]) => `${k === "credit" ? "Udhaar" : k} ₹${Number(v).toFixed(2)}`).join(" + ")}
            </div>
          )}
          {Number(invoice.due_amount || 0) > 0 && (
            <div className="flex justify-between" data-testid="receipt-due-row"><span className="text-red-600 font-semibold">Balance Due (Udhaar)</span><span className="font-mono-num font-bold text-red-600">₹{Number(invoice.due_amount).toFixed(2)}{invoice.credit_due_date ? ` • ${invoice.credit_due_date}` : ""}</span></div>
          )}
        </div>

        <div className="mt-4 border-t border-dashed border-slate-300 pt-3 text-center">
          <div className="text-[10px] text-slate-500">Thank you for shopping with us • Get well soon!</div>
          <div className="text-[10px] text-slate-400 mt-0.5">Computer generated invoice — no signature required</div>
          <div className="text-[10px] text-emerald-800 font-semibold mt-0.5">trustmedspharma.com</div>
          <div className="text-[9px] text-slate-400 mt-1.5">Medicines once sold will not be taken back. Please check expiry before use.</div>
        </div>
      </div>
    </div>
  );
}
